import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  UserCircleIcon,
  PencilIcon,
  KeyIcon,
  BellIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  CreditCardIcon,
  BanknotesIcon,
  CalendarIcon,
  PhotoIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useGetProfileQuery, useUpdateProfileMutation, useChangePasswordMutation } from '../../services/authApi';
import { useGetMyCompanyQuery } from '../../services/companyApi';
import { useDispatch } from 'react-redux';
import { logoutUser } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
  const { t } = useTranslation(['profile', 'common']);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Profile data
  const { data: profileData, isLoading: profileLoading, error: profileError, refetch } = useGetProfileQuery();
  const { data: companyData } = useGetMyCompanyQuery();
  const [updateProfile, { isLoading: updatingProfile }] = useUpdateProfileMutation();
  const [changePassword, { isLoading: changingPassword }] = useChangePasswordMutation();

  // Form states
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    avatar: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthLabel = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return { label: 'Very Weak', color: 'bg-red-500' };
      case 2:
        return { label: 'Weak', color: 'bg-orange-500' };
      case 3:
        return { label: 'Fair', color: 'bg-yellow-500' };
      case 4:
        return { label: 'Good', color: 'bg-blue-500' };
      case 5:
        return { label: 'Strong', color: 'bg-green-500' };
      default:
        return { label: 'Very Weak', color: 'bg-red-500' };
    }
  };

  // Password requirements
  const passwordRequirements = [
    { text: 'At least 8 characters', met: passwordForm.newPassword.length >= 8 },
    { text: 'One lowercase letter', met: /[a-z]/.test(passwordForm.newPassword) },
    { text: 'One uppercase letter', met: /[A-Z]/.test(passwordForm.newPassword) },
    { text: 'One number', met: /[0-9]/.test(passwordForm.newPassword) },
    { text: 'One special character', met: /[^A-Za-z0-9]/.test(passwordForm.newPassword) }
  ];

  const strength = getPasswordStrengthLabel(passwordStrength);

  // Initialize form data
  useEffect(() => {
    if (profileData?.data?.user || profileData?.user || profileData) {
      const profile = profileData.data?.user || profileData.user || profileData;
      const employee = profile.employee || profile;

      // For admin roles, use user data directly; for employee roles, use employee data
      const isEmployeeRole = ['employee', 'manager', 'hr_admin'].includes(user?.role);

      setProfileForm({
        firstName: isEmployeeRole ? (employee.firstName || profile.firstName || '') : (profile.firstName || ''),
        lastName: isEmployeeRole ? (employee.lastName || profile.lastName || '') : (profile.lastName || ''),
        phone: isEmployeeRole ? (employee.phone || profile.phone || '') : (profile.phone || ''),
        avatar: employee.avatar || profile.avatar || ''
      });
    }
  }, [profileData, user?.role]);

  // Handle profile form submission
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage('');

    // Only send firstName and lastName, exclude phone and avatar since they're read-only
    const updateData = {
      firstName: profileForm.firstName,
      lastName: profileForm.lastName
    };

    try {
      await updateProfile(updateData).unwrap();
      setSuccessMessage('Profile updated successfully!');
      // Refetch profile data to ensure we have the latest data
      await refetch();
    } catch (error) {
      setErrors(error.data?.errors || { general: 'Failed to update profile' });
    }
  };

  // Handle password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage('');

    // Validate password strength
    if (passwordStrength < 4) {
      setErrors({ newPassword: 'Password must meet at least "Good" strength requirements' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }).unwrap();
      setSuccessMessage('Password changed successfully! Logging out in 3 seconds...');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength(0);

      // Logout after 3 seconds
      setTimeout(async () => {
        try {
          await dispatch(logoutUser()).unwrap();
          navigate('/login');
        } catch (error) {
          console.error('Logout failed:', error);
          navigate('/login');
        }
      }, 3000);
    } catch (error) {
      setErrors(error.data?.errors || { general: 'Failed to change password' });
    }
  };

  // Role-based content
  const getRoleBasedContent = () => {
    const role = user?.role;
    const company = companyData?.data;

    switch (role) {
      case 'super_admin':
        return {
          title: 'Super Administrator Profile',
          subtitle: 'Manage system-wide settings and all companies',
          icon: SparklesIcon,
          gradient: 'from-purple-600 via-pink-600 to-red-600',
          features: [
            'System Administration',
            'Company Management',
            'Plan Management',
            'Payment Processing',
            'Global Reports',
            'System Monitoring'
          ],
          permissions: [
            'Create/Manage Companies',
            'Configure Plans & Pricing',
            'Process Payments',
            'Access All Data',
            'System Configuration',
            'User Management'
          ]
        };

      case 'company_admin':
        return {
          title: 'Company Administrator Profile',
          subtitle: `Manage ${company?.name || 'your company'} operations`,
          icon: BuildingOfficeIcon,
          gradient: 'from-blue-600 via-indigo-600 to-purple-600',
          features: [
            'Employee Management',
            'Payroll Processing',
            'Leave Management',
            'Attendance Tracking',
            'Reports & Analytics',
            'Company Settings'
          ],
          permissions: [
            'Manage Employees',
            'Process Payroll',
            'Approve Leave Requests',
            'Configure Company Settings',
            'Generate Reports',
            'Manage Departments'
          ]
        };

      case 'hr_admin':
        return {
          title: 'HR Administrator Profile',
          subtitle: 'Handle human resources operations',
          icon: UsersIcon,
          gradient: 'from-green-600 via-teal-600 to-cyan-600',
          features: [
            'Employee Onboarding',
            'Leave Management',
            'Attendance Monitoring',
            'Performance Reviews',
            'HR Reports',
            'Employee Records'
          ],
          permissions: [
            'Onboard Employees',
            'Manage Leave Policies',
            'Monitor Attendance',
            'Conduct Performance Reviews',
            'Generate HR Reports',
            'Update Employee Records'
          ]
        };

      case 'manager':
        return {
          title: 'Manager Profile',
          subtitle: 'Oversee team operations and performance',
          icon: ChartBarIcon,
          gradient: 'from-orange-600 via-red-600 to-pink-600',
          features: [
            'Team Management',
            'Leave Approvals',
            'Attendance Reports',
            'Performance Tracking',
            'Team Analytics',
            'Resource Allocation'
          ],
          permissions: [
            'Approve Team Leave',
            'View Team Attendance',
            'Monitor Performance',
            'Access Team Reports',
            'Manage Team Resources',
            'Team Communication'
          ]
        };

      case 'employee':
        return {
          title: 'Employee Profile',
          subtitle: 'Manage your personal information and work data',
          icon: UserCircleIcon,
          gradient: 'from-primary-600 to-primary-700',
          features: [
            'Personal Information',
            'Attendance Tracking',
            'Leave Requests',
            'Salary Slips',
            'Performance Data',
            'Company Policies'
          ],
          permissions: [
            'Update Personal Info',
            'Clock In/Out',
            'Request Leave',
            'View Salary Slips',
            'Access Performance',
            'View Company Policies'
          ]
        };

      default:
        return {
          title: 'User Profile',
          subtitle: 'Manage your account information',
          icon: UserCircleIcon,
          gradient: 'from-gray-600 to-gray-700',
          features: ['Basic Profile Management'],
          permissions: ['Update Profile', 'Change Password']
        };
    }
  };

  const roleContent = getRoleBasedContent();

  if (profileLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600 font-medium">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-8 text-center shadow-soft">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-red-800 mb-2">Error Loading Profile</h3>
          <p className="text-red-700 mb-6">Unable to load your profile information. Please try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 shadow-soft hover:shadow-hard transform hover:scale-105"
          >
            <ArrowRightIcon className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <Helmet>
        <title>{roleContent.title} - {t('common:appName')}</title>
      </Helmet>
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{roleContent.title}</h1>
            <p className="text-gray-600">{roleContent.subtitle}</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-white/20 backdrop-blur-sm text-white shadow-soft border border-white/30">
              <ShieldCheckIcon className="h-4 w-4 mr-2" />
              {user?.role?.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Modern Hero Header */}
      <div className={`bg-gradient-to-br ${roleContent.gradient} rounded-3xl shadow-xl overflow-hidden relative mb-8`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-y-32 -translate-x-32"></div>
        </div>

        <div className="relative px-8 py-12 text-white">
          <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-6 lg:space-y-0 lg:space-x-8">
            <div className="flex-shrink-0">
              <div className="h-24 w-24 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-xl">
                <roleContent.icon className="h-12 w-12" />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-2xl lg:text-3xl font-bold mb-2">Welcome back, {profileForm.firstName || 'User'}!</h2>
                  <p className="text-white/90 text-lg mb-4">Manage your account settings and preferences</p>
                </div>
              </div>
            </div>
          </div>

          {/* Role Features Section */}
          <div className="bg-white/5 backdrop-blur-sm border-t border-white/10 mt-8">
            <div className="px-8 py-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="text-white/90 font-semibold flex items-center text-sm">
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Available Features
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {roleContent.features.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-white/15 backdrop-blur-sm text-white border border-white/20 shadow-soft"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-white/90 font-semibold flex items-center text-sm">
                    <KeyIcon className="h-4 w-4 mr-2" />
                    Your Permissions
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {roleContent.permissions.map((permission, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500/20 backdrop-blur-sm text-green-100 border border-green-400/30"
                      >
                        <CheckCircleIcon className="h-3 w-3 mr-1.5" />
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-gray-50/50">
            <nav className="flex space-x-1 px-6">
              {[
                { id: 'personal', label: 'Personal Information', icon: UserCircleIcon },
                { id: 'security', label: 'Security Settings', icon: KeyIcon },
                { id: 'company', label: 'Company Information', icon: BuildingOfficeIcon, roles: ['super_admin', 'company_admin', 'hr_admin'] }
              ].filter(tab => !tab.roles || tab.roles.includes(user?.role)).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-6 border-b-3 font-semibold text-sm transition-all duration-300 rounded-t-lg ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-700 bg-white shadow-sm'
                      : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-white/50'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-3" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-8">
            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
              <div className="space-y-8 p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                    <p className="text-gray-600 mt-1">Update your personal details and contact information</p>
                  </div>
                  <div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <UserCircleIcon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>

                {successMessage && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CheckCircleIcon className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-800">{successMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleProfileSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-secondary-800 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={profileForm.firstName}
                          onChange={(e) => setProfileForm({...profileForm, firstName: e.target.value})}
                          className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                          placeholder="Enter your first name"
                          required
                        />
                        <UserCircleIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-secondary-800 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={profileForm.lastName}
                          onChange={(e) => setProfileForm({...profileForm, lastName: e.target.value})}
                          className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                          placeholder="Enter your last name"
                          required
                        />
                        <UserCircleIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-secondary-800 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                        disabled
                        className="w-full px-4 py-3 pl-12 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed shadow-sm"
                        placeholder="Enter your phone number"
                      />
                      <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center">
                      <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                      Phone number cannot be changed. Contact administrator if needed.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-secondary-800 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="w-full px-4 py-3 pl-12 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed shadow-sm"
                      />
                      <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center">
                      <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                      Email cannot be changed. Contact administrator if needed.
                    </p>
                  </div>

                  <div className="flex justify-end pt-6 border-t border-secondary-100">
                    <button
                      type="submit"
                      disabled={updatingProfile}
                      className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:ring-4 focus:ring-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      {updatingProfile ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                          Updating Profile...
                        </>
                      ) : (
                        <>
                          <PencilIcon className="h-5 w-5 mr-3" />
                          Update Profile
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Security Settings Tab */}
            {activeTab === 'security' && (
              <div className="space-y-8 p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Security Settings</h2>
                    <p className="text-gray-600 mt-1">Manage your password and security preferences</p>
                  </div>
                  <div className="h-12 w-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <KeyIcon className="h-6 w-6 text-red-600" />
                  </div>
                </div>

                {successMessage && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CheckCircleIcon className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-800">{successMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="space-y-8">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-secondary-800 mb-2">
                      Current Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword.current ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                        className="w-full px-4 py-3 pl-12 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        placeholder="Enter current password"
                        required
                      />
                      <KeyIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                      <button
                        type="button"
                        onClick={() => setShowPassword({...showPassword, current: !showPassword.current})}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors duration-200"
                      >
                        {showPassword.current ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-secondary-800 mb-2">
                        New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword.new ? 'text' : 'password'}
                          value={passwordForm.newPassword}
                          onChange={(e) => {
                            setPasswordForm({...passwordForm, newPassword: e.target.value});
                            setPasswordStrength(calculatePasswordStrength(e.target.value));
                          }}
                          className={`w-full px-4 py-3 pl-12 pr-12 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md ${
                            errors.newPassword ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter new password"
                          required
                          minLength={8}
                        />
                        <KeyIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <button
                          type="button"
                          onClick={() => setShowPassword({...showPassword, new: !showPassword.new})}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors duration-200"
                        >
                          {showPassword.new ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.newPassword && (
                        <p className="text-sm text-red-600 mt-2 flex items-center">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          {errors.newPassword}
                        </p>
                      )}

                      {/* Password Strength Indicator */}
                      {passwordForm.newPassword && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Password Strength</span>
                            <span className={`text-sm font-medium ${getPasswordStrengthLabel(passwordStrength).color.replace('bg-', 'text-').replace('-500', '-600')}`}>
                              {getPasswordStrengthLabel(passwordStrength).label}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthLabel(passwordStrength).color}`}
                              style={{ width: `${(passwordStrength / 5) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-secondary-800 mb-2">
                        Confirm New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword.confirm ? 'text' : 'password'}
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                          className={`w-full px-4 py-3 pl-12 pr-12 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md ${
                            errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Confirm new password"
                          required
                        />
                        <KeyIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <button
                          type="button"
                          onClick={() => setShowPassword({...showPassword, confirm: !showPassword.confirm})}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors duration-200"
                        >
                          {showPassword.confirm ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-sm text-red-600 mt-2 flex items-center">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          {errors.confirmPassword}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-blue-800 mb-3">Password Requirements</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {passwordRequirements.map((requirement, index) => (
                            <div key={index} className="flex items-center text-sm">
                              {requirement.met ? (
                                <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                              ) : (
                                <div className="h-4 w-4 rounded-full border-2 border-blue-300 mr-2 flex-shrink-0"></div>
                              )}
                              <span className={requirement.met ? 'text-green-700' : 'text-blue-700'}>
                                {requirement.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-6 border-t border-secondary-100">
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 focus:ring-4 focus:ring-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      {changingPassword ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                          Changing Password...
                        </>
                      ) : (
                        <>
                          <KeyIcon className="h-5 w-5 mr-3" />
                          Change Password
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Company Information Tab */}
            {activeTab === 'company' && ['super_admin', 'company_admin', 'hr_admin'].includes(user?.role) && (
              <div className="space-y-8 p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
                    <p className="text-gray-600 mt-1">View and manage company details</p>
                  </div>
                  <div className="h-12 w-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <BuildingOfficeIcon className="h-6 w-6 text-indigo-600" />
                  </div>
                </div>

                {companyData?.data ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <BuildingOfficeIcon className="h-5 w-5 mr-2 text-blue-600" />
                          Basic Information
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Company Name
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.name}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Legal Name
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.legalName || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Website
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.website || 'Not specified'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-6 border border-green-100">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <svg className="h-5 w-5 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          Contact Information
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Email
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.email}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Phone
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.phone || 'Not specified'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <svg className="h-5 w-5 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Location & Settings
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Country
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.address?.country || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Currency
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.currency}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Employee ID Prefix
                            </label>
                            <p className="text-gray-900 font-medium">{companyData.data.employeeIdPrefix || 'EMP'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <Cog6ToothIcon className="h-5 w-5 mr-2 text-orange-600" />
                          System Settings
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Weekly Off Days
                            </label>
                            <p className="text-gray-900 font-medium">
                              {companyData.data.weeklyOffDays?.join(', ') || 'Saturday, Sunday'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              Attendance Type
                            </label>
                            <p className="text-gray-900 font-medium">
                              {companyData.data.settings?.attendance?.type || 'System'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                      <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Information Unavailable</h3>
                    <p className="text-gray-600">Unable to load company details at this time.</p>
                  </div>
                )}
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default Profile;